/**
 * Session documents — what a session keeps beside its results.
 *
 * A document is a recording, a video, a transcript, or any other file or link
 * that lets someone who was not in the room follow what happened. Stored in
 * `session_artifacts` (migration 330; files since 818). Each document shows on
 * the session's lineage timeline and bubbles up to the track and cycle views,
 * and is snapshotted into `session_reports.deliberation_snapshot` (726).
 *
 * Canon: Doc 21 §6.4 (the Deliberation Record's recording and transcript
 * vectors). Roadmap D-5. Brief K §2a.
 */

/**
 * The six kinds. The first three were there from 330 (a link or pasted text);
 * the `_file` three arrived with 818 and point at an object in the private
 * `session-documents` bucket through `storagePath`.
 */
export type SessionArtifactType =
  | 'ai_transcript'
  | 'video_link'
  | 'document_link'
  | 'audio_file'
  | 'video_file'
  | 'document_file';

export const SESSION_FILE_ARTIFACT_TYPES: readonly SessionArtifactType[] = [
  'audio_file',
  'video_file',
  'document_file',
] as const;

export function isFileArtifactType(type: SessionArtifactType): boolean {
  return SESSION_FILE_ARTIFACT_TYPES.includes(type);
}

export interface SessionArtifact {
  id: string;
  sessionId: string;
  workspaceId: string;
  artifactType: SessionArtifactType;
  /** Human-readable label shown in the timeline. */
  title: string;
  /** External URL (video link, hosted transcript, or doc). */
  url?: string | null;
  /** Pasted / imported transcript body (for ai_transcript when no hosted URL). */
  content?: string | null;
  /** Optional annotation / description. */
  notes?: string | null;
  /** Object path in the session-documents bucket; set for the `_file` types only. */
  storagePath?: string | null;
  /** The uploaded file's media type. */
  mimeType?: string | null;
  /** The uploaded file's size in bytes. */
  sizeBytes?: number | null;
  createdBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/** Timeline event types added to the provenance timeline for artifacts. */
export type ArtifactTimelineEventType =
  | 'artifact_transcript'
  | 'artifact_video'
  | 'artifact_document';
